import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon.jsx';

const { FiHelpCircle, FiChevronDown, FiChevronUp } = FiIcons;

const FAQ = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [openItems, setOpenItems] = useState(new Set());

  const toggleItem = (index) => {
    const newOpenItems = new Set(openItems);
    if (newOpenItems.has(index)) {
      newOpenItems.delete(index);
    } else {
      newOpenItems.add(index);
    }
    setOpenItems(newOpenItems);
  };

  const faqItems = [
    {
      question: 'How accurate are these cost estimates?',
      answer: 'The fuel-only estimates are quite accurate if your input data is correct. However, total generator costs can be 50-100% higher when including maintenance, repairs, and other operational expenses. Use these results as a starting point for financial planning.'
    },
    {
      question: 'What costs are not included in this calculation?',
      answer: 'This calculator only covers fuel costs. It excludes generator purchase price, installation, maintenance, repairs, oil changes, air filters, staff time, insurance, theft, and downtime costs. The optional all-in estimate adds 20-40% to approximate some of these.'
    },
    {
      question: 'How do I measure my average load accurately?',
      answer: 'If possible, use a power meter to measure actual consumption during typical generator operation. Otherwise, add up the wattage of all equipment typically running (lights, equipment, etc.) and convert to kilowatts (divide by 1000).'
    },
    {
      question: 'What if my generator usage varies significantly month to month?',
      answer: 'Use average values over several months, or calculate for your typical "high usage" month to understand worst-case costs. You can run multiple calculations for different scenarios.'
    },
    {
      question: 'How does this compare to grid electricity costs?',
      answer: 'Compare your calculated cost per kWh to your local grid electricity rates. In many developing markets, generator costs range from £0.20-0.50+ per kWh, while grid electricity might be £0.05-0.15 per kWh when available.'
    },
    {
      question: 'Can I use this for different fuel types?',
      answer: 'This calculator is designed for diesel generators. For petrol or gas generators, the principles are the same, but efficiency and costs per litre/cubic metre will differ significantly.'
    }
  ];

  return (
    <section className="py-16 px-4 bg-gray-50">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="w-full px-8 py-6 text-left hover:bg-gray-50 transition-colors focus:outline-none focus:ring-4 focus:ring-violet-200"
            aria-expanded={isOpen}
            aria-controls="faq-content"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <SafeIcon 
                  icon={FiHelpCircle} 
                  className="text-2xl text-violet-600" 
                  aria-hidden="true"
                />
                <h2 className="text-2xl font-bold text-gray-900">
                  Frequently Asked Questions
                </h2>
              </div>
              <SafeIcon 
                icon={isOpen ? FiChevronUp : FiChevronDown} 
                className="text-xl text-gray-500" 
                aria-hidden="true"
              />
            </div>
          </button>

          <AnimatePresence>
            {isOpen && (
              <motion.div
                id="faq-content"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3 }}
                className="border-t border-gray-200"
              >
                <div className="px-8 py-6">
                  <div className="space-y-4">
                    {faqItems.map((item, index) => (
                      <div key={index} className="border border-gray-200 rounded-lg">
                        <button
                          onClick={() => toggleItem(index)}
                          className="w-full px-6 py-4 text-left hover:bg-gray-50 transition-colors focus:outline-none focus:ring-2 focus:ring-violet-200 rounded-lg"
                          aria-expanded={openItems.has(index)}
                        >
                          <div className="flex items-center justify-between">
                            <h3 className="font-semibold text-gray-900 pr-4">
                              {item.question}
                            </h3>
                            <SafeIcon 
                              icon={openItems.has(index) ? FiChevronUp : FiChevronDown} 
                              className="text-lg text-gray-500 flex-shrink-0" 
                              aria-hidden="true"
                            />
                          </div>
                        </button>
                        
                        <AnimatePresence>
                          {openItems.has(index) && (
                            <motion.div
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: 'auto' }}
                              exit={{ opacity: 0, height: 0 }}
                              transition={{ duration: 0.2 }}
                              className="border-t border-gray-200"
                            >
                              <div className="px-6 py-4">
                                <p className="text-gray-700">
                                  {item.answer}
                                </p>
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
};

export default FAQ;